import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { STALE_TIME } from "@/constants";
import { UserContext } from "@/context/User/UserContext";
import { cn } from "@/lib/utils";
import { getAllDepartments, getSystemUsers } from "@/services/auth-functions";
import { JobJustificationCreate } from "@/services/hiring-justification-functions";
import { CreateJobJustificationParams } from "@/types/hiring-justification.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronsUpDown, Plus, Trash2 } from "lucide-react";
import { useContext, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { toast } from "sonner";
import { z } from "zod";

const formSchema = z.object({
	job: z.string().min(1, {
		message: "Job title is required",
	}),
	departmentid: z.number(),
	shortdescription: z.string().min(1, {
		message: "Description is required",
	}),
	key_responsibilities: z.array(
		z.object({
			value: z.string(),
		})
	),
	employment_justification: z.string().min(1, {
		message: "Employment justification is required",
	}),
	academic_qualification: z.string().min(1, {
		message: "Academic qualification is required",
	}),
	experience: z.string().min(1, {
		message: "Experience is required",
	}),
	budgeted_amount: z.number(),
	finance_partner_approver: z.number(),
});

const CreateJob = () => {
	const user = useContext(UserContext);
	const [suOpen, setSuOpen] = useState(false);
	const [departmentOpen, setDepartmentOpen] = useState(false);
	const [showSuccess, setShowSuccess] = useState(false);

	const departments = useQuery({
		queryKey: ["all-departments"],
		queryFn: () => getAllDepartments(user?.accessToken!),
		staleTime: STALE_TIME,
	});

	const systemUsers = useQuery({
		queryKey: ["systemusers"],
		queryFn: () => getSystemUsers(user?.accessToken!),
		staleTime: STALE_TIME,
		enabled: !!user?.accessToken,
		onSuccess: () => {
			if (!user) return;
			if (!user.check) return;
			if (!user.check.manageremail) return;
		},
	});

	const EXPERIENCE_LIST = [
		"No experience",
		"1-2 years experience",
		"3-5 years experience",
		"6+ years experience",
	];

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			shortdescription: "",
			key_responsibilities: [{ value: "" }],
		},
	});

	const jobCreate = useMutation({
		mutationKey: ["job_create"],
		mutationFn: (data: CreateJobJustificationParams) =>
			JobJustificationCreate(user?.accessToken!, data),
		onSuccess: () => {
			toast.success("Job justification created successfully", {
				description: "The job justification has been created successfully.",
			});
			form.reset({
				job: "",
				departmentid: 0,
				shortdescription: "",
				key_responsibilities: [{ value: "" }],
				employment_justification: "",
				academic_qualification: "",
				experience: "",
				budgeted_amount: 0,
				finance_partner_approver: 0,
			});
			setShowSuccess(true);
			jobCreate.reset();
		},
		onError: (err) => {
			toast.error("Error creating job justification");
			console.log(err);
		},
	});

	const { fields, append, remove } = useFieldArray({
		control: form.control,
		name: "key_responsibilities",
	});

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		const responsibilities = data.key_responsibilities
			.filter((item) => item.value.length > 0)
			.map((item) => item.value);
		const body: CreateJobJustificationParams = {
			job: data.job,
			departmentid: data.departmentid,
			shortdescription: data.shortdescription,
			key_responsibilities: responsibilities,
			employment_justification: data.employment_justification,
			academic_qualification: data.academic_qualification,
			experience: data.experience,
			budgeted_amount: data.budgeted_amount,
			finance_partner_approver: data.finance_partner_approver,
		};
		jobCreate.mutate(body);
	};

	const disableFields = () => {
		if (jobCreate.isLoading) return true;
		if (jobCreate.isSuccess) return true;
		return false;
	};

	// const scroll = () => {
	// 	if (errorElementRef.current && error) {
	// 		errorElementRef.current.scrollIntoView({
	// 			behavior: "smooth",
	// 			block: "end",
	// 		});
	// 	}
	// };

	// useEffect(() => {
	// 	scroll();
	// }, [error]);

	if (showSuccess) {
		return (
			<div className="flex justify-center items-center min-h-screen">
				<Card className="max-w-2xl mx-auto gap-2">
					<CardHeader className="flex justify-center mb-4">
						<CardTitle className="text-2xl">
							Job Authorization Created Successfully
						</CardTitle>
					</CardHeader>
					<CardContent className="text-center mb-4">
						<p>Your job justification has been created successfully.</p>
					</CardContent>
					<CardFooter className="flex justify-center ">
						<Button onClick={() => setShowSuccess(false)}>Create Another</Button>
					</CardFooter>
				</Card>
			</div>
		);
	}

	return (
		<Card className="max-w-2xl mx-auto gap-2">
			<CardHeader>
				<CardTitle>Create Job Justification</CardTitle>
			</CardHeader>
			<CardContent>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<FormField
							name="job"
							render={({ field }) => (
								<FormItem className="mb-3">
									<FormLabel>Job Title</FormLabel>
									<FormControl>
										<Input {...field} disabled={disableFields()} />
									</FormControl>
									<FormMessage className="font-light" />
								</FormItem>
							)}
						/>
						{departments.isLoading ? (
							<span>Loading Departments</span>
						) : departments.data ? (
							<FormField
								name="departmentid"
								render={({ field }) => (
									<FormItem className="flex flex-col mb-2">
										<FormLabel>Department</FormLabel>
										<Popover
											open={departmentOpen}
											onOpenChange={(open) => setDepartmentOpen(open)}
										>
											<PopoverTrigger asChild>
												<FormControl>
													<Button
														variant="outline"
														role="combobox"
														className={cn(
															"justify-between font-normal",
															!field.value && "text-muted-foreground"
														)}
													>
														{field.value
															? departments.data.find(
																	(department) => department.departmentid === field.value
															  )?.departmentname
															: "Select department"}
														<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
													</Button>
												</FormControl>
											</PopoverTrigger>
											<PopoverContent className="p-0 w-[calc(100vw-2rem)] sm:w-[400px]">
												<Command>
													<CommandInput placeholder="Search department..." />
													<CommandList>
														<CommandEmpty>No account found.</CommandEmpty>
														<CommandGroup>
															{departments.data.map((item) => (
																<CommandItem
																	value={item.departmentname}
																	key={item.departmentid.toString()}
																	onSelect={() => {
																		form.setValue("departmentid", item.departmentid);
																		setDepartmentOpen(false);
																	}}
																>
																	<Check
																		className={cn(
																			"mr-2 h-4 w-4",
																			item.departmentid.toString() === field.value
																				? "opacity-100"
																				: "opacity-0"
																		)}
																	/>
																	{item.departmentname}
																</CommandItem>
															))}
														</CommandGroup>
													</CommandList>
												</Command>
											</PopoverContent>
										</Popover>
										<FormMessage />
									</FormItem>
								)}
							/>
						) : null}
						<FormField
							name="shortdescription"
							render={({ field }) => (
								<FormItem className="mb-2">
									<FormLabel>Description</FormLabel>
									<FormControl>
										<Textarea {...field} disabled={disableFields()} />
									</FormControl>
									<FormMessage className="font-light" />
								</FormItem>
							)}
						/>
						<FormLabel>Key Responsibilities</FormLabel>
						{fields.map((field, index) => (
							<FormField
								key={field.id}
								control={form.control}
								name={`key_responsibilities.${index}`}
								render={({ field }) => (
									<FormItem className="flex items-center gap-2">
										<FormControl>
											<Input
												placeholder={`Responsibility ${index + 1}`}
												value={field.value.value}
												onChange={(e) => field.onChange({ value: e.target.value })}
												disabled={disableFields()}
											/>
										</FormControl>
										<Button
											type="button"
											variant="destructive"
											size="sm"
											onClick={() => remove(index)}
											disabled={disableFields()}
											className="space-y-0"
										>
											<Trash2 className="h-4 w-4" />
										</Button>
										<FormMessage />
									</FormItem>
								)}
							/>
						))}
						<Button
							type="button"
							onClick={() => append({ value: "" })}
							className="mt-2 text-sm font-normal mb-4"
							size="sm"
							disabled={disableFields()}
						>
							<Plus className="mr-2 h-4 w-4" />
							Add Responsibility
						</Button>
						<FormField
							name="employment_justification"
							render={({ field }) => (
								<FormItem className="mb-2">
									<FormLabel>Employment Justification</FormLabel>
									<FormControl>
										<Textarea {...field} disabled={disableFields()} />
									</FormControl>
									<FormMessage className="font-light" />
								</FormItem>
							)}
						/>
						<FormField
							name="academic_qualification"
							render={({ field }) => (
								<FormItem className="mb-2">
									<FormLabel>Academic Qualification</FormLabel>
									<FormControl>
										<Textarea {...field} disabled={disableFields()} />
									</FormControl>
									<FormMessage className="font-light" />
								</FormItem>
							)}
						/>
						<FormField
							name="experience"
							render={({ field }) => (
								<FormItem className="mb-2">
									<FormLabel>Experience</FormLabel>
									<FormControl>
										<Select disabled={disableFields()} onValueChange={field.onChange}>
											<SelectTrigger>
												<SelectValue placeholder="Select an option" />
											</SelectTrigger>
											<SelectContent>
												{EXPERIENCE_LIST.map((item) => (
													<SelectItem value={item}>{item}</SelectItem>
												))}
											</SelectContent>
										</Select>
									</FormControl>
									<FormMessage className="font-light" />
								</FormItem>
							)}
						/>
						<FormField
							name="budgeted_amount"
							render={({ field }) => (
								<FormItem className="mb-4">
									<FormLabel>Budgeted Amount</FormLabel>
									<FormControl>
										<Input
											type="number"
											disabled={disableFields()}
											onChange={(e) => field.onChange(e.target.valueAsNumber)}
										/>
									</FormControl>
									<FormMessage className="font-light" />
								</FormItem>
							)}
						/>

						{systemUsers.isLoading ? (
							<span>Loading Users</span>
						) : systemUsers.data ? (
							<FormField
								name="finance_partner_approver"
								render={({ field }) => (
									<FormItem className="flex flex-col mb-2">
										<FormLabel>Finance Partner Approver:</FormLabel>
										<Popover open={suOpen} onOpenChange={(open) => setSuOpen(open)}>
											<PopoverTrigger asChild>
												<FormControl>
													<Button
														variant="outline"
														role="combobox"
														className={cn(
															"justify-between font-normal",
															!field.value && "text-muted-foreground"
														)}
														disabled={disableFields()}
													>
														{field.value
															? systemUsers.data.find((user) => user.id === field.value)
																	?.username
															: "Select approver"}
														<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
													</Button>
												</FormControl>
											</PopoverTrigger>
											<PopoverContent className="p-0 w-[calc(100vw-2rem)] sm:w-[400px]">
												<Command>
													<CommandInput placeholder="Search account..." />
													<CommandList>
														<CommandEmpty>No account found.</CommandEmpty>
														<CommandGroup>
															{systemUsers.data.map((item) => (
																<CommandItem
																	value={item.username}
																	key={item.id}
																	onSelect={() => {
																		form.setValue("finance_partner_approver", item.id);
																		setSuOpen(false);
																	}}
																>
																	<Check
																		className={cn(
																			"mr-2 h-4 w-4",
																			item.id.toString() === field.value
																				? "opacity-100"
																				: "opacity-0"
																		)}
																	/>
																	{item.username}
																</CommandItem>
															))}
														</CommandGroup>
													</CommandList>
												</Command>
											</PopoverContent>
										</Popover>
										<FormMessage />
									</FormItem>
								)}
							/>
						) : null}
						<Button
							type="submit"
							className="mt-4 w-full"
							// disabled={invoiceCreate.isLoading || invoiceCreate.isSuccess}
						>
							{jobCreate.isLoading ? "Creating..." : "Submit Claim"}
						</Button>
					</form>
				</Form>
			</CardContent>
		</Card>
	);
};

export default CreateJob;
